/**
 * Payment sheet — shown after creating a CryptoPay invoice.
 *
 * Polls the invoice every 4s so we react quickly when the user finishes paying
 * inside Telegram. On status === "paid" we celebrate, refresh subscription, and
 * auto-close after a moment. The "pay" CTA opens the CryptoPay mini-app inside
 * Telegram via tg.openTelegramLink (web fallback uses window.open).
 */

import { motion } from "framer-motion";
import { CheckCircle2, ExternalLink, Loader2, Wallet } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";

import { Button, Sheet } from "@/design";
import { useInvoice, type Invoice, type Plan } from "@/api/billing";
import { cn } from "@/lib/cn";
import { haptic, tg } from "@/lib/telegram";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  plan: Plan | null;
}

export function PaymentSheet({ open, onClose, invoice, plan }: Props) {
  const qc = useQueryClient();
  const { data: live } = useInvoice(invoice?.id ?? null, {
    refetchInterval: open && invoice?.status === "pending" ? 4_000 : false,
  });
  const current = live ?? invoice;
  const paid = current?.status === "paid";
  const isStars = current?.provider === "telegram_stars";

  // When the invoice flips to paid, refresh global subscription/me and close.
  useEffect(() => {
    if (!paid) return;
    haptic.notify("success");
    qc.invalidateQueries({ queryKey: ["billing", "subscription"] });
    qc.invalidateQueries({ queryKey: ["billing", "invoices"] });
    qc.invalidateQueries({ queryKey: ["me"] });
    const t = setTimeout(onClose, 2200);
    return () => clearTimeout(t);
  }, [paid, qc, onClose]);

  const handleOpenPay = () => {
    if (!current?.pay_url) return;
    haptic.impact("medium");
    if (tg?.openInvoice && isStars) {
      tg.openInvoice(current.pay_url);
    } else if (tg) {
      tg.openTelegramLink(current.pay_url);
    } else {
      window.open(current.pay_url, "_blank", "noopener");
    }
  };

  const handleCopy = async () => {
    if (!current?.pay_url) return;
    try {
      await navigator.clipboard.writeText(current.pay_url);
      toast.success("Ссылка скопирована");
      haptic.select();
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={paid ? "Оплачено ✨" : "Оплата подписки"}
      description={
        paid
          ? "Подписка активирована"
          : isStars
            ? "Оплатите счёт в Telegram Stars — статус обновится автоматически"
            : "Оплатите счёт в CryptoBot — обновим статус автоматически"
      }
    >
      <div className="space-y-5">
        {/* Invoice card */}
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "relative overflow-hidden rounded-3xl border border-white/[0.06]",
            "bg-gradient-to-br from-[#1A1A1F] via-[#15131A] to-[#0F0F12]",
            "p-6",
          )}
        >
          <div
            className="absolute -top-20 -right-20 size-64 rounded-full bg-accent/20 blur-3xl"
            aria-hidden
          />
          <div className="relative space-y-4">
            <div className="flex items-center gap-2">
              <div className="size-10 rounded-2xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.6)]">
                {paid ? (
                  <CheckCircle2 className="size-5 text-white" />
                ) : (
                  <Wallet className="size-5 text-white" />
                )}
              </div>
              <div>
                <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
                  {plan?.title ?? current?.plan_title ?? "Подписка"}
                </div>
                <div className="text-sm font-semibold">
                  {plan?.duration_days ?? 0} дней доступа
                </div>
              </div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-[42px] leading-none font-semibold tabular-nums tracking-tight">
                ${current?.amount_usd.toFixed(2) ?? "0.00"}
              </span>
              <span className="text-text-muted text-sm">{isStars ? "≈ Stars" : "USDT"}</span>
            </div>

            <StatusRow status={current?.status ?? "pending"} />

            <div className="pt-3 border-t border-white/[0.06] text-[11px] text-text-muted">
              Счёт #{current?.invoice_id ?? "—"}
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        {!paid && (
          <div className="space-y-2">
            <Button
              fullWidth
              leftIcon={<ExternalLink />}
              onClick={handleOpenPay}
              disabled={!current?.pay_url}
            >
              {isStars ? "Оплатить Stars" : "Открыть CryptoBot"}
            </Button>
            <Button
              variant="ghost"
              fullWidth
              onClick={handleCopy}
              disabled={!current?.pay_url}
            >
              Скопировать ссылку
            </Button>
          </div>
        )}

        {paid && (
          <Button fullWidth onClick={onClose}>
            Готово
          </Button>
        )}
      </div>
    </Sheet>
  );
}

function StatusRow({ status }: { status: Invoice["status"] }) {
  if (status === "paid") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-success/15 text-success text-[12px] font-medium">
        <CheckCircle2 className="size-3.5" />
        Оплачено
      </div>
    );
  }
  if (status === "expired" || status === "canceled") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-danger/15 text-danger text-[12px] font-medium">
        Истёк срок счёта
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] text-text-muted text-[12px] font-medium">
      <Loader2 className="size-3.5 animate-spin" />
      Ожидаем оплату
    </div>
  );
}
